/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import { MenuItem, Select, Slider, Typography, type SelectChangeEvent } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import {
  incidentRuntimeAtom,
  incidentSectionRuntimes,
  selectedRunTimeAtom,
} from '../Incident/atoms';
import { IncidentState } from '../Incident/types';
import { Toast } from '../components/Toast';
import { disabledModelsFor, settledIncidentConfigByIdAtom } from '../config/api';
import { PanelField, panelSelectSx, panelSliderSx } from './layerControls/panelFields';
import { TEXT_DIM } from './layerControls/palette';

type Props = {
  incidentId: string;
  sectionId: keyof IncidentState;
};

const NO_RUNS_MESSAGE =
  'No data available. This page will update as soon as new data feeds become available';
const MODEL_RUN_HINT =
  'Which run of the model the map shows. A section opens on its newest run; pick an earlier ' +
  'one from the list or with the slider.';

/**
 * The model runs available for an incident section and the one on the map.
 * Selecting a run evicts the previous run's cached data. Choosing the run a
 * section opens on is IncidentRunAvailability's job, not this hook's, so the
 * choice is made once however many controls read the runs.
 */
export const useIncidentRuns = (incidentId: string, sectionId: keyof IncidentState) => {
  const incidentConfig = useAtomValue(settledIncidentConfigByIdAtom(incidentId));
  const [selectedRuntime, setSelectedRuntime] = useAtom(
    selectedRunTimeAtom(`${incidentId}-${sectionId}`)
  );
  const prevRuntimeRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    prevRuntimeRef.current = selectedRuntime?.time;
  }, [selectedRuntime?.time]);

  const availableTimes = useAtomValue(
    incidentSectionRuntimes({
      incidentId,
      sectionId,
      disabledModels: disabledModelsFor(incidentConfig, sectionId),
    })
  );

  const selectRuntime = React.useCallback(
    (time: number) => {
      if (prevRuntimeRef.current != null) {
        incidentRuntimeAtom.remove({
          incidentId,
          sectionId,
          lastUpdatedOn: prevRuntimeRef.current,
        });
      }
      setSelectedRuntime({
        time,
        comments:
          availableTimes.find(t => t.lastupdatedon === time)?.comments ??
          new Date(time).toLocaleString(),
      });
    },
    [incidentId, sectionId, setSelectedRuntime, availableTimes]
  );

  return { availableTimes, selectedRuntime, selectRuntime };
};

/**
 * Mounted on every incident view for each section it draws, whether or not
 * the Map Controls panel is open: while nothing is selected it chooses the
 * newest run, so a section always opens on its latest data, and it shows the
 * "no data" notice for a section with no runs at all.
 */
export const IncidentRunAvailability: React.FC<Props> = ({ incidentId, sectionId }) => {
  const { availableTimes, selectedRuntime, selectRuntime } = useIncidentRuns(incidentId, sectionId);

  React.useEffect(() => {
    if (availableTimes.length > 0 && selectedRuntime == null) {
      selectRuntime(availableTimes[availableTimes.length - 1].lastupdatedon);
    }
  }, [availableTimes, selectedRuntime, selectRuntime]);

  return <Toast open={availableTimes.length === 0} message={NO_RUNS_MESSAGE} severity="error" />;
};

/** The "Model run" control of the Map Controls panel. */
export const IncidentDateSelect = React.memo((props: Props) => {
  const { availableTimes, selectedRuntime, selectRuntime } = useIncidentRuns(
    props.incidentId,
    props.sectionId
  );

  const handleChange = React.useCallback(
    (event: SelectChangeEvent<number | ''>) => {
      if (typeof event.target.value === 'number') {
        selectRuntime(event.target.value);
      }
    },
    [selectRuntime]
  );

  const handleSliderChange = React.useCallback(
    (_event: Event, value: number | number[]) => {
      if (typeof value === 'number') {
        selectRuntime(availableTimes[value].lastupdatedon);
      }
    },
    [availableTimes, selectRuntime]
  );

  return (
    <PanelField id="model-run-label" label="Model run" hint={MODEL_RUN_HINT}>
      {availableTimes.length === 0 ? (
        <Typography variant="caption" sx={{ display: 'block', color: TEXT_DIM }}>
          {NO_RUNS_MESSAGE}
        </Typography>
      ) : (
        <>
          <Select
            fullWidth
            size="small"
            labelId="model-run-label"
            value={selectedRuntime?.time ?? ''}
            onChange={handleChange}
            sx={panelSelectSx}
            inputProps={{ name: 'dateRange', id: 'dateRange' }}
          >
            {[...availableTimes]
              .sort((a, b) => b.lastupdatedon - a.lastupdatedon)
              .map(time => {
                const date = new Date(time.lastupdatedon);
                return (
                  <MenuItem key={time.lastupdatedon} value={time.lastupdatedon}>
                    {date.toLocaleDateString()} {date.toLocaleTimeString()} {time.comments}
                  </MenuItem>
                );
              })}
          </Select>
          {availableTimes.length > 1 && (
            <Slider
              size="small"
              track={false}
              aria-labelledby="model-run-label"
              min={0}
              max={availableTimes.length - 1}
              value={availableTimes.map(a => a.lastupdatedon).indexOf(selectedRuntime?.time ?? 0)}
              sx={{ ...panelSliderSx, mt: 0.5 }}
              onChange={handleSliderChange}
            />
          )}
        </>
      )}
    </PanelField>
  );
});
